// A thin facade over SoundService (sound.service.ts). Kept for the `$sounds`
// API some callers still use; it delegates every call to the canonical `sound`
// singleton, so the whole library shares one voice, one persisted volume/mute,
// and one master limiter. The feeling palette itself lives in sound.service.ts.
import { sound } from './sound.service'

export type HappyFeeling =
	| 'joyful'
	| 'content'
	| 'excited'
	| 'proud'
	| 'hopeful'
	| 'relieved'
	| 'grateful'
	| 'peaceful'
	| 'playful'
	| 'amused'
	| 'curious'
	| 'inspired'
	| 'confident'
	| 'loved'
	| 'comforted'
	| 'energized'
	| 'celebrated'

export type SadFeeling =
	| 'sad'
	| 'lonely'
	| 'disappointed'
	| 'heartbroken'
	| 'grieving'
	| 'hopeless'
	| 'empty'
	| 'discouraged'
	| 'melancholic'
	| 'homesick'
	| 'hurt'
	| 'miserable'
	| 'regretful'
	| 'ashamed'
	| 'inferior'

export type AnxiousFeeling =
	| 'anxious'
	| 'worried'
	| 'afraid'
	| 'terrified'
	| 'panicked'
	| 'nervous'
	| 'uneasy'
	| 'insecure'
	| 'overwhelmed'
	| 'stressed'
	| 'tense'
	| 'apprehensive'
	| 'startled'
	| 'suspicious'
	| 'vulnerable'

export type AngryFeeling =
	| 'annoyed'
	| 'irritated'
	| 'frustrated'
	| 'angry'
	| 'enraged'
	| 'bitter'
	| 'resentful'
	| 'jealous'
	| 'envious'
	| 'indignant'
	| 'impatient'
	| 'hostile'
	| 'contemptuous'

export type TiredFeeling =
	'tired' | 'exhausted' | 'drained' | 'burnedOut' | 'numb' | 'bored' | 'unmotivated' | 'apathetic' | 'restless'

export type CalmFeeling =
	'calm' | 'relaxed' | 'atEase' | 'balanced' | 'stable' | 'secure' | 'safe' | 'centered' | 'grounded' | 'accepting'

export type ConnectedFeeling =
	| 'connected'
	| 'accepted'
	| 'included'
	| 'belonging'
	| 'appreciated'
	| 'valued'
	| 'respected'
	| 'supported'
	| 'protective'
	| 'compassionate'
	| 'empathetic'

export type MixedFeeling =
	| 'conflicted'
	| 'confused'
	| 'ambivalent'
	| 'nostalgic'
	| 'guilty'
	| 'embarrassed'
	| 'surprised'
	| 'shocked'
	| 'awestruck'
	| 'skeptical'

export type Feeling =
	| HappyFeeling
	| SadFeeling
	| AnxiousFeeling
	| AngryFeeling
	| TiredFeeling
	| CalmFeeling
	| ConnectedFeeling
	| MixedFeeling

// FeelingCategory lives in ../types/mood-audio.types.ts, re-exported from ./index.ts.

export class EmotionalSoundGenerator {
	setVolume(vol: number): void {
		sound.setVolume(vol)
	}

	getVolume(): number {
		return sound.volume
	}

	mute(): void {
		sound.mute()
	}

	unmute(): void {
		sound.unmute()
	}

	isMuted(): boolean {
		return sound.muted
	}

	play(feeling: Feeling, opts?: { dropIfSuspended?: boolean }): void {
		sound.play(feeling, opts)
	}

	dispose(): void {
		sound.dispose()
	}
}

/** Global emotional sound player */
export const $sounds = new EmotionalSoundGenerator()
